import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

interface LearningDialogProps {
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const LearningDialog: React.FC<LearningDialogProps> = ({ title, description, onClose, children, footer }) => {
  const { isDark } = useTheme();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    close.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href]');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('keydown', keydown); previous?.focus(); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`flex max-h-[95dvh] w-full flex-col overflow-hidden rounded-t-2xl border shadow-xl sm:max-h-[min(896px,calc(100dvh-2rem))] sm:max-w-[720px] sm:rounded-2xl ${isDark ? 'border-sminy-dark-border bg-sminy-dark-elevated text-sminy-dark-text-primary' : 'border-sminy-border bg-white text-sminy-text-primary'}`}
      >
        <div className={`flex shrink-0 items-start justify-between gap-3 border-b p-5 sm:px-6 ${isDark ? 'border-sminy-dark-border' : 'border-sminy-border'}`}>
          <div>
            <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
            {description && <p id={descriptionId} className={`mt-1 text-sm ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>{description}</p>}
          </div>
          <button ref={close} type="button" onClick={onClose} aria-label="Fechar" className="grid min-h-11 min-w-11 place-items-center rounded-xl hover:bg-sminy-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:px-6">{children}</div>
        {footer && <div className={`flex shrink-0 flex-col-reverse gap-2 border-t p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6 ${isDark ? 'border-sminy-dark-border' : 'border-sminy-border'}`}>{footer}</div>}
      </div>
    </div>
  );
};
